'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useRef } from 'react';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';

// How long after a `resize` event to wait before trusting it. Debounced
// rather than immediate: `resize` fires continuously through a live drag,
// and there is no correctness reason to re-measure on every one of those --
// only once the size has actually stopped changing.
const RESIZE_DEBOUNCE_MS = 120;

// How long after mount to keep re-measuring every animation frame.
//
// ResizeObserver only fires when the element changes its OWN border box; it
// says nothing about the element MOVING because something else on the page
// reflowed -- a web font swapping in, an image finishing layout. Without
// this the anchor freezes at wherever the engine sat on the one frame the
// first measure ran, which reads as a small constant offset.
const SETTLE_MS = 1000;
export interface EngineAnchor {
  // The engine's centre, in CSS px relative to the canvas box.
  centerX: number;
  centerY: number;
  // THE ENGINE'S SIDE in CSS px -- the artwork's drawn size, not the box's.
  //
  // One number, because the artwork is square (1022x1022) and drawn
  // `contain`: such an image always renders at `min(boxWidth, boxHeight)`,
  // centred in the box. Taking the min rather than the width keeps that
  // true whatever the box turns out to be -- and the two slides already
  // size their boxes by opposite axes (intro by width, story by height,
  // `w-auto` + `aspect-1-1`), so assuming either one would be a guess.
  size: number;
  // False until the first successful measure. The canvas must not place a
  // quad from a zeroed anchor.
  isMeasured: boolean;
}
export const createEngineAnchor = (): EngineAnchor => ({
  centerX: 0,
  centerY: 0,
  size: 0,
  isMeasured: false
});
interface UseEngineAnchorOptions {
  // THE SLOT -- the engine's untransformed layout box, not the inner
  // transformed div. See the note below.
  elementRef: RefObject<HTMLElement | null>;
  // The wrapper the canvas fills, read for its POSITION only, to convert
  // the engine's rect into canvas-relative space.
  canvasBoxRef: RefObject<HTMLElement | null>;
  anchorRef: MutableRef<EngineAnchor>;
  // Whether THIS slide currently owns the anchor. Both slides mount their
  // engine at once (the slider only toggles opacity), so without this the
  // two would fight over one ref every frame.
  enabled: boolean;
}

/**
 * Follows the engine's SETTLED on-screen box, so things welded to the
 * engine can be placed on it.
 *
 * MEASURE THE SLOT, NOT THE INNER TRANSFORMED DIV.
 *
 * The slot is the engine's layout box and never moves. The inner div
 * carries the hand-off tween's `translate` and `scale`, which walk the
 * engine in from the clip's framing -- so measuring THAT gives whatever
 * size the engine happens to be mid-flight, and at the START of the tween
 * that is the clip's framing rather than the layout.
 *
 * Everything anchored here appears only once the engine has SETTLED -- the
 * aura ignites as it lands -- and at that moment the inner div's transform
 * is identity, so the slot's box IS the engine's box. Measuring the slot
 * gets that answer on the first frame and holds it, instead of chasing an
 * animation whose early values are precisely the wrong ones.
 *
 * It also means both slides' settle tweens stay COMPLETELY UNTOUCHED, and
 * that there is no per-frame reading to do at all: a layout box only
 * changes on layout, which the observers below already cover.
 *
 * DOM MEASUREMENT NEVER HAPPENS IN THE R3F RENDER LOOP. A
 * `getBoundingClientRect` inside `useFrame` forces a synchronous reflow
 * every frame. An earlier rig in the home scene did it per frame and was
 * removed for the jank -- see useFrameMask, whose measurement discipline
 * this follows.
 */
export const useEngineAnchor = t0 => {
  const $ = _c(5);
  const {
    elementRef,
    canvasBoxRef,
    anchorRef,
    enabled
  } = t0;
  const anchorWriteRef = useRef(anchorRef);
  let t1;
  let t2;
  if ($[0] !== canvasBoxRef || $[1] !== elementRef || $[2] !== enabled) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      const element = elementRef.current;
      const canvasBox = canvasBoxRef.current;
      if (!element || !canvasBox) {
        return;
      }
      const written = anchorWriteRef.current;
      const measure = () => {
        const rect = element.getBoundingClientRect();
        const canvasRect = canvasBox.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
          return;
        }
        written.value.centerX = rect.left + rect.width / 2 - canvasRect.left;
        written.value.centerY = rect.top + rect.height / 2 - canvasRect.top;
        written.value.size = Math.min(rect.width, rect.height);
        written.value.isMeasured = true;
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      let settleFrame;
      const settleUntil = performance.now() + SETTLE_MS;
      const settle = () => {
        measure();
        if (performance.now() < settleUntil) {
          settleFrame = requestAnimationFrame(settle);
        }
      };
      settleFrame = requestAnimationFrame(settle);
      let resizeTimer;
      const handleResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(measure, RESIZE_DEBOUNCE_MS);
      };
      window.addEventListener("resize", handleResize);
      return () => {
        observer.disconnect();
        if (settleFrame !== undefined) {
          cancelAnimationFrame(settleFrame);
        }
        window.clearTimeout(resizeTimer);
        window.removeEventListener("resize", handleResize);
      };
    };
    t2 = [elementRef, canvasBoxRef, enabled];
    $[0] = canvasBoxRef;
    $[1] = elementRef;
    $[2] = enabled;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
};